// Cmd-hold cheatsheet.
//
// When the writer presses and holds Cmd (Ctrl on non-Mac) for longer
// than HOLD_MS, a bottom-anchored toast appears listing every Cmd-
// shortcut grouped by category. The toast hides on:
//   - Cmd release (keyup of Meta/Control)
//   - Any chord keydown — the moment the writer commits to a shortcut,
//     the toast gets out of the way
//   - Window blur (Cmd-Tab to another app)
//
// The HOLD_MS delay (350ms) is the same delay macOS uses for tooltip-
// like hover affordances. It's long enough that a Cmd-Tab to switch
// apps never flashes the toast, and short enough that a writer who
// genuinely wants to discover shortcuts doesn't feel the lag.
//
// Self-contained: appends one root <div> to <body> and owns its
// listeners. Importing this module is a no-op; call initCmdCheatsheet()
// once after the DOM is ready.

const HOLD_MS = 350;

// Source of truth for what's displayed. Order within each group is
// frequency-of-use, not alphabetical — newcomers' eyes land on the
// most common shortcut first.
const GROUPS = [
  {
    title: 'Elements',
    items: [
      { keys: ['⌘', '1'], label: 'Scene Heading' },
      { keys: ['⌘', '2'], label: 'Action' },
      { keys: ['⌘', '3'], label: 'Character' },
      { keys: ['⌘', '4'], label: 'Dialogue' },
      { keys: ['⌘', '5'], label: 'Parenthetical' },
      { keys: ['⌘', '6'], label: 'Transition' },
      { keys: ['⌘', 'K'], label: 'Elements palette' },
    ],
  },
  {
    title: 'Format',
    items: [
      { keys: ['⌘', 'B'], label: 'Bold' },
      { keys: ['⌘', 'I'], label: 'Italic' },
      { keys: ['⌘', 'U'], label: 'Underline' },
    ],
  },
  {
    title: 'Editing',
    items: [
      { keys: ['⌘', 'Z'], label: 'Undo' },
      { keys: ['⌘', '⇧', 'Z'], label: 'Redo' },
      { keys: ['⌘', 'F'], label: 'Find' },
      { keys: ['⌘', 'A'], label: 'Select all' },
      { keys: ['⌘', 'C'], label: 'Copy' },
      { keys: ['⌘', 'V'], label: 'Paste' },
      { keys: ['⌘', 'X'], label: 'Cut' },
    ],
  },
  {
    title: 'Notes',
    items: [{ keys: ['⌘', '⌥', 'N'], label: 'Scratchpad for this scene' }],
  },
  {
    title: 'File',
    items: [
      { keys: ['⌘', 'S'], label: 'Save' },
      { keys: ['⌘', 'N'], label: 'New script' },
      { keys: ['⌘', 'O'], label: 'Open' },
      { keys: ['⌘', 'P'], label: 'Print / Export PDF' },
    ],
  },
];

function isMac() {
  // Electron exposes navigator.platform; on macOS this is "MacIntel" or
  // similar starting with "Mac". Falling back to userAgent keeps this
  // robust against Apple silicon eventually changing the platform string.
  if (typeof navigator === 'undefined') return true;
  const p = navigator.platform || '';
  if (p.toLowerCase().includes('mac')) return true;
  const ua = navigator.userAgent || '';
  return /Mac|iPhone|iPad/i.test(ua);
}

function modifierLabel() {
  return isMac() ? '⌘' : 'Ctrl';
}

function isModifierKey(event) {
  // Mac: Meta = ⌘. Non-Mac: Ctrl. We also accept the matching e.key
  // string so a Linux user holding Ctrl gets the same treatment.
  if (isMac()) return event.key === 'Meta';
  return event.key === 'Control';
}

function renderGroup(group, mod) {
  const itemsHtml = group.items
    .map((item) => {
      const keys = item.keys
        .map((k) => `<span class="cmd-cheatsheet-key">${k === '⌘' ? mod : k}</span>`)
        .join('<span class="cmd-cheatsheet-plus">+</span>');
      return (
        `<div class="cmd-cheatsheet-item">` +
        `<span class="cmd-cheatsheet-chord">${keys}</span>` +
        `<span class="cmd-cheatsheet-label">${item.label}</span>` +
        `</div>`
      );
    })
    .join('');
  return (
    `<div class="cmd-cheatsheet-group">` +
    `<div class="cmd-cheatsheet-group-title">${group.title}</div>` +
    itemsHtml +
    `</div>`
  );
}

export function initCmdCheatsheet() {
  if (typeof document === 'undefined') return;
  // Idempotent — calling twice (e.g., HMR) is a no-op after the first.
  if (document.querySelector('.cmd-cheatsheet')) return;

  const el = document.createElement('div');
  el.className = 'cmd-cheatsheet';
  el.setAttribute('aria-hidden', 'true');
  const mod = modifierLabel();
  el.innerHTML = GROUPS.map((g) => renderGroup(g, mod)).join('');
  document.body.appendChild(el);

  let showTimer = null;
  let visible = false;
  let modDown = false;

  function show() {
    if (visible) return;
    visible = true;
    el.classList.add('visible');
    // Mark the gesture as discovered so the tips toast can auto-hide
    // its "Hold ⌘ to see every shortcut" tip.
    try {
      localStorage.setItem('usedCmdCheatsheet', '1');
    } catch {
      /* ignore */
    }
  }

  // Expose show() so the tips toast can offer a "Show me" preview of
  // the cheatsheet from its Cmd-hold tip. Tied to this initCmdCheatsheet
  // invocation so the same closure-bound DOM element is targeted.
  window.LastslateActions = window.LastslateActions || {};
  window.LastslateActions.showCmdCheatsheet = show;
  function hide() {
    if (showTimer !== null) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    if (!visible) return;
    visible = false;
    el.classList.remove('visible');
  }

  document.addEventListener(
    'keydown',
    (event) => {
      if (isModifierKey(event)) {
        if (modDown) return; // ignore auto-repeat
        modDown = true;
        if (showTimer !== null) clearTimeout(showTimer);
        showTimer = setTimeout(show, HOLD_MS);
        return;
      }
      // Chord pressed while the modifier is held → writer committed to
      // a shortcut, get out of the way immediately.
      if (modDown || visible) hide();
    },
    true,
  );

  document.addEventListener(
    'keyup',
    (event) => {
      if (!isModifierKey(event)) return;
      modDown = false;
      hide();
    },
    true,
  );

  // Cmd-Tab, switching workspaces, Spotlight, etc. all blur the window
  // while leaving Meta in an indeterminate state. Treat blur as release.
  window.addEventListener('blur', () => {
    modDown = false;
    hide();
  });
}
